/**
 * @engine/player - PlayerApplication
 *
 * The main game application class.
 * Extends Application/Subsystem (high-level lifecycle) and registers a
 * bridge ISystem on the Engine singleton so that **one** requestAnimationFrame
 * drives everything: physics, scripts, sprite sync, rendering.
 *
 * Frame order (driven by Engine → GameLoop):
 *   fixedUpdate →  update → lateUpdate → render
 *
 * Inside each callback we forward to Application.fixedUpdate / .update
 * which dispatch to all registered Subsystems.
 */

import {
  Application,
  Engine,
  type ISystem,
  type Level,
  SubsystemPriority,
  SceneEventsSubsystem,
  resetTransformStorage,
  resetSpriteRendererStorage,
} from '@engine/core';

import type {
  PlayerConfig,
  ResolvedPlayerConfig,
  LoadProgress,
  LoadProgressCallback,
} from './types';

import { setGraphicsConfig } from '@engine/render2d';

import { AssetSubsystem } from './subsystems/AssetSubsystem';
import { InputSubsystem } from './subsystems/InputSubsystem';
import { RenderSubsystem } from './subsystems/RenderSubsystem';
import { ScriptSubsystem } from './subsystems/ScriptSubsystem';
import { LevelSubsystem } from './subsystems/LevelSubsystem';
import { CameraRigSubsystem } from './subsystems/CameraRigSubsystem';
import { PhysicsSubsystem } from './subsystems/PhysicsSubsystem';
import { AudioSubsystem } from './subsystems/AudioSubsystem';
import { VFXSubsystem } from './subsystems/VFXSubsystem';
import { UISubsystem } from './subsystems/UISubsystem';

// ============================================================================
// PlayerApplication
// ============================================================================

/**
 * @example
 * ```ts
 * const player = new PlayerApplication({
 *   canvas: document.getElementById('game') as HTMLCanvasElement,
 *   entryLevel: 'main',
 *   assetBase: './assets/',
 * });
 *
 * await player.initializeWithProgress();
 * player.start();
 * ```
 */
export class PlayerApplication extends Application {
  readonly name = 'PlayerApplication';

  private readonly config: ResolvedPlayerConfig;
  private progressCallback?: LoadProgressCallback;

  constructor(config: PlayerConfig) {
    super();

    // The deferred render path (engine default) produces no visible sprites in the
    // .NET ClearScript player — the GBuffer → light-accumulation stage comes out empty
    // (the same path works in the browser). Forward+ keeps lighting but skips the
    // GBuffer, which renders sprites correctly here. Deferred stays the editor default.
    setGraphicsConfig({ renderPath: 'forward+' });

    this.config = {
      canvas: config.canvas,
      assetBase: config.assetBase ?? './assets/',
      entryLevel: config.entryLevel,
      renderBackend: config.renderBackend ?? 'webgpu',
      targetFPS: config.targetFPS ?? 60,
      fixedDeltaTime: config.fixedDeltaTime ?? 1 / 60,
      debug: config.debug ?? false,
      manifest: config.manifest ?? 'manifest.json',
      physics: config.physics ?? false,
      wasmUrl: config.wasmUrl ?? '',
    };

    // --- Register subsystems in priority order ---

    this.registerSubsystem(new SceneEventsSubsystem());
    this.registerSubsystem(new AssetSubsystem({
      assetBase: this.config.assetBase,
      manifest: this.config.manifest,
    }));

    this.registerSubsystem(new InputSubsystem({
      canvas: this.config.canvas,
    }));

    this.registerSubsystem(new RenderSubsystem({
      canvas: this.config.canvas,
    }));

    this.registerSubsystem(new ScriptSubsystem()); // unified core.Script (TS + Redprint graphs)
    this.registerSubsystem(new CameraRigSubsystem());
    this.registerSubsystem(new PhysicsSubsystem({
      wasmUrl: this.config.wasmUrl || undefined,
    }));
    this.registerSubsystem(new AudioSubsystem());
    this.registerSubsystem(new VFXSubsystem({
      debug: this.config.debug,
    }));

    this.registerSubsystem(new UISubsystem({
      canvas: this.config.canvas,
    }));

    this.registerSubsystem(new LevelSubsystem({
      entryLevel: this.config.entryLevel,
    }));
  }

  // ==========================================================================
  // ACCESSORS
  // ==========================================================================

  get assets(): AssetSubsystem {
    return this.getSubsystem<AssetSubsystem>('assets')!;
  }

  get input(): InputSubsystem {
    return this.getSubsystem<InputSubsystem>('input')!;
  }

  get render(): RenderSubsystem {
    return this.getSubsystem<RenderSubsystem>('render')!;
  }

  get scriptSubsystem(): ScriptSubsystem | undefined {
    return this.getSubsystem<ScriptSubsystem>('script');
  }

  get levels(): LevelSubsystem {
    return this.getSubsystem<LevelSubsystem>('level')!;
  }

  get currentLevel(): Level | null {
    return this.levels?.currentLevel ?? null;
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  /**
   * Initialize with optional progress reporting.
   * Resets SoA storages, boots Engine singleton, then runs base Application.initialize().
   */
  async initializeWithProgress(onProgress?: LoadProgressCallback): Promise<void> {
    this.progressCallback = onProgress;

    this.setEvents({
      onSubsystemInitialized: (subsystem) => {
        this.reportProgress({
          phase: 'init',
          progress: 0.5,
          currentItem: subsystem.config.name,
        });
      },
      onReady: () => {
        this.reportProgress({ phase: 'ready', progress: 1 });
      },
    });

    // Reset SoA stores before anything touches them
    resetTransformStorage();
    resetSpriteRendererStorage();

    // Boot Engine singleton (creates GameLoop + Time)
    const engine = Engine.instance;
    await engine.init({
      fixedDeltaTime: this.config.fixedDeltaTime,
    });

    // Register a bridge ISystem that dispatches into Application subsystems.
    // Engine.update() also calls scene.processPendingDestroy() which we want.
    engine.registerSystem(this.createBridgeSystem());

    await this.initialize();
  }

  /**
   * Start the game.
   * Delegates to Application.start() and then kicks off the Engine's GameLoop.
   */
  override start(): void {
    super.start();
    Engine.instance.start();
  }

  /**
   * Load and switch to a new level.
   */
  async loadLevel(levelId: string): Promise<void> {
    await this.levels.loadLevel(levelId);
  }

  /**
   * Pause the game loop and all subsystems.
   */
  override pause(): void {
    Engine.instance.pause();
    super.pause();
  }

  /**
   * Resume the game loop and all subsystems.
   */
  override resume(): void {
    super.resume();
    Engine.instance.resume();
  }

  /**
   * Shut everything down.
   */
  override async shutdown(): Promise<void> {
    Engine.instance.stop();
    await super.shutdown();
    Engine.reset();
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  /**
   * Creates an ISystem that bridges Engine loop callbacks → Application subsystem dispatch.
   */
  private createBridgeSystem(): ISystem {
    const app = this;
    // Isolate each frame phase. A throw in one phase — notably a Rapier WASM
    // "unreachable" trap in physics fixedUpdate — must NOT skip render() or stop
    // GameLoop from scheduling the next frame; otherwise a single physics error
    // freezes the whole game (frozen image, no sprites, resize can't redraw).
    // Errors are logged once per phase to avoid per-frame console spam.
    const loggedPhaseErrors = new Set<string>();
    const guard = (label: string, fn: () => void): void => {
      try {
        fn();
      } catch (err) {
        if (!loggedPhaseErrors.has(label)) {
          loggedPhaseErrors.add(label);
          console.error(`[player-bridge] ${label} failed (subsequent errors suppressed):`, err);
        }
      }
    };
    return {
      name: 'player-bridge',
      fixedUpdate() {
        guard('fixedUpdate', () => app.fixedUpdate(Engine.instance.time.fixedDeltaTime));
      },
      update() {
        guard('update', () => app.update(Engine.instance.time.deltaTime));
      },
      lateUpdate() {
        // Application doesn't have lateUpdate dispatch.
        // Call specific subsystems that need it.
        guard('lateUpdate', () => {
          const dt = Engine.instance.time.deltaTime;
          app.scriptSubsystem?.lateUpdate(dt);
        });
      },
      render() {
        guard('render', () => app.render.renderFrame());
      },
    };
  }

  private reportProgress(progress: LoadProgress): void {
    this.progressCallback?.(progress);
  }
}

// Legacy alias
export { PlayerApplication as PlayerRuntime };
